import { Box, Stack } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import SearchBar from "components/SearchBar";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { MeetingTemplate } from "features/Resources/MeetingTemplate/models";
import { useMemo, useState } from "react";
import { useMeetingTemplateListQuery } from "features/Resources/MeetingTemplate/hooks";
import { InternalLink } from "components/Link";
import { Text12 } from "components/Text";
import { createQueryString } from "helpers/queryString";
import { meetingTemplatePathById } from "helpers/url/pages/v2/actionPaths";
import TemplateListEmptyState from "features/Resources/SharedTemplate/Components/TemplateListEmptyState";
import { TemplateTableDeleteButton } from "features/Resources/SharedTemplate/Components/TemplateTableButtons";
import { DisplayTemplatePaths } from "features/Resources/SharedTemplate/Components/DisplayTemplatePaths";
import { DisplayTemplateAttendeesColumn } from "features/Resources/MeetingTemplate/Components/DisplayTemplateAttendees";
import CDS, { AdminMeetingTypeTag } from "design-system";
import TriggerTiming from "components/Display/TriggerTiming";

const columnHelper = createColumnHelper<MeetingTemplate>();

const columns = [
  columnHelper.accessor("meetingType", {
    header: "",
    cell: ({ getValue }) => <AdminMeetingTypeTag.IconBadge meetingType={getValue()} />,
    maxSize: 43,
    minSize: 43,
    size: 43,
    enableSorting: false,
    meta: {
      sx: {
        paddingLeft: "24px !important",
        paddingRight: "0 !important",
        width: "56px !important",
        maxWidth: "56px !important",
        minWidth: "56px !important",
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "name",
    header: "Name",
    enableSorting: false,
    cell: (row) => (
      <Box width="fit-content">
        <InternalLink
          to={meetingTemplatePathById(row.getValue().id)}
          textDecoration="none"
          _hover={{ textDecoration: "none" }}
          width="fit-content"
        >
          <Text12 wordBreak="break-word" fontWeight="bold">
            {row.getValue()?.name}
          </Text12>
        </InternalLink>
      </Box>
    ),
    meta: {
      sx: {
        paddingLeft: "0 !important",
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "attendees",
    header: "Attendees",
    enableSorting: false,
    cell: (row) => <DisplayTemplateAttendeesColumn meetingTemplateId={row.getValue()?.id} />,
  }),
  columnHelper.accessor((row) => row, {
    id: "duration",
    header: "Duration",
    enableSorting: false,
    cell: (row) => <CDS.Text14 color="fg.muted">{`${row.getValue().durationMinutes}m`}</CDS.Text14>,
    meta: {
      sx: {
        width: "80px",
        maxWidth: "80px",
        minWidth: "80px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "scheduled",
    header: "When",
    enableSorting: false,
    cell: (row) => <TriggerTiming descriptor={row.getValue().proposedAtDescriptor} />,
  }),
  columnHelper.accessor((row) => row, {
    id: "pathIds",
    header: "Paths",
    enableSorting: false,
    cell: (row) => <DisplayTemplatePaths template={row.getValue()} />,
    meta: {
      sx: {
        width: "100px",
        maxWidth: "100px",
        minWidth: "100px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "buttons",
    header: "",
    enableSorting: false,
    cell: (row) => (
      <TemplateTableDeleteButton templateSummary={{ templateType: "meeting_template", template: row.getValue() }} />
    ),
    meta: {
      sx: {
        width: "60px",
        maxWidth: "60px",
        minWidth: "60px",
      },
      headerHStackProps: { justifyContent: "flex-end" },
      cellHStackProps: { justifyContent: "flex-end" },
    },
  }),
];

export default function MeetingTemplates() {
  const [searchText, setSearchText] = useState("");

  const queryString = useMemo(
    () =>
      createQueryString({
        pagination: { pageIndex: 0, pageSize: 200 },
        additionalQueries: {
          q: searchText && searchText.length > 2 ? searchText : undefined,
        },
      }),
    [searchText],
  );

  const { data } = useMeetingTemplateListQuery(queryString);
  const sortedData = useMemo(
    () => [...(data || [])].sort((a, b) => (a.sortValue ?? Infinity) - (b.sortValue ?? Infinity)),
    [data],
  );

  return (
    <Stack direction="column" gap="4" w="100%">
      <Stack direction="row" w="100%" alignItems="center" justifyContent="flex-start">
        <SearchBar minWidth="500" onInput={setSearchText} />
      </Stack>
      <ControlledDataTable<MeetingTemplate>
        data={sortedData}
        columns={columns}
        initialPagination={{ pageSize: 200, pageIndex: 0 }}
        emptyStateComponent={<TemplateListEmptyState actionType="meeting" hasActiveFilters={searchText.length > 2} />}
      />
    </Stack>
  );
}
